// ADR-0010 Fatia 1 (Trilha 3) — keypair RSA-OAEP-2048 por membro do comitê.
//
// A privada NUNCA sai em claro: gerada no browser, exportada PKCS8 e
// imediatamente embrulhada sob a KEK_membro (AES-GCM-256). Servidor/admin
// NUNCA decifram (ADR-0002 + ADR-0010 §2.6). O CI grep R-Phase-2.6 (Plan 02)
// trava import de unwrapMemberPrivateKey/unwrapDekWithMemberPrivate em
// apps/api + apps/web-admin — só o browser do membro destranca.
//
// Estas funções rodam NO BROWSER (web-comite no enroll/leitura, web-intake no
// wrap-no-submit da Fatia 2) — por isso base64 browser-safe (btoa/atob, paridade
// bytes.ts dos apps), NÃO Buffer.

// TS 6.x: crypto.subtle exige ArrayBuffer, não ArrayBufferLike (paridade escrow.ts).
function toBuffer(arr: Uint8Array): ArrayBuffer {
  return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer;
}

// base64 browser-safe (btoa/atob existem em browser e Node 16+). Paridade
// apps/web-comite/src/lib/bytes.ts — NÃO usar Buffer (ausente no browser).
function bytesToB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}
function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** Parâmetros do par — RSA-OAEP-2048/SHA-256, paridade do cofre (escrow.ts). */
const RSA_PARAMS = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
} as const;

export interface WrappedMemberKeypair {
  /** JSON.stringify do JWK público (só componentes públicas — sem `d`). */
  readonly publicKeyJwk: string;
  /** base64 de AES-GCM(PKCS8 da privada) sob a KEK_membro. NUNCA o PKCS8 cru. */
  readonly privateKeyCiphertext: string;
  /** base64 do IV AES-GCM de 12 bytes pareado com privateKeyCiphertext. */
  readonly nonce: string;
}

/**
 * Gera o par RSA-OAEP-2048 no browser e embrulha a privada (PKCS8) sob a
 * KEK_membro (AES-GCM-256). Retorna SÓ material seguro de subir: pública (clara)
 * + privada-embrulhada + nonce. A privada em claro NUNCA deixa esta função.
 */
export async function generateAndWrapMemberKeypair(
  kekRaw: Uint8Array,
): Promise<WrappedMemberKeypair> {
  const keypair = await crypto.subtle.generateKey(RSA_PARAMS, true, ['wrapKey', 'unwrapKey']);

  const publicJwk = await crypto.subtle.exportKey('jwk', keypair.publicKey);
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', keypair.privateKey));

  // Embrulha o PKCS8 sob a KEK_membro (AES-GCM). IV fresco por enrollment.
  const aesKey = await crypto.subtle.importKey(
    'raw',
    toBuffer(kekRaw),
    { name: 'AES-GCM' },
    false,
    ['encrypt'],
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, toBuffer(pkcs8));

  return {
    publicKeyJwk: JSON.stringify(publicJwk),
    privateKeyCiphertext: bytesToB64(new Uint8Array(ct)),
    nonce: bytesToB64(iv),
  };
}

/**
 * Destranca a privada-embrulhada com a KEK_membro e a importa como CryptoKey
 * RSA-OAEP (não-extraível — defesa em profundidade: nunca re-exporta o PKCS8).
 * KEK errada → AES-GCM lança (tag fail). Roda no browser do membro.
 */
export async function unwrapMemberPrivateKey(
  privateKeyCiphertextB64: string,
  nonceB64: string,
  kekRaw: Uint8Array,
): Promise<CryptoKey> {
  const aesKey = await crypto.subtle.importKey(
    'raw',
    toBuffer(kekRaw),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );
  const iv = b64ToBytes(nonceB64);
  const ct = b64ToBytes(privateKeyCiphertextB64);
  const pkcs8 = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBuffer(iv) },
    aesKey,
    toBuffer(ct),
  );

  return crypto.subtle.importKey(
    'pkcs8',
    pkcs8,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false, // não-extraível
    ['decrypt', 'unwrapKey'],
  );
}

/**
 * RE-EMBRULHA a privada do membro de uma KEK para outra — caminho (a) da troca de
 * senha (28/07). Sem isto, trocar a senha torna a privada inabrível e o membro perde
 * a leitura de TODAS as denúncias, para sempre (provado em
 * `web-comite/lib/troca-senha-destroi-acesso.test.ts`).
 *
 * ⚠️ OPERA NO NÍVEL DE BYTES, de propósito: a privada é AES-GCM(PKCS8, KEK), então
 * decifrar com a KEK velha e cifrar com a nova NÃO exige importar a chave. O
 * invariante "a privada é CryptoKey NÃO-EXTRAÍVEL" fica INTACTO — nunca existe um
 * CryptoKey exportável aqui. Os bytes PKCS8 vivem em memória por instantes, igual ao
 * que `unwrapMemberPrivateKey` já faz internamente (nenhuma exposição nova), e são
 * ZERADOS no finally.
 *
 * A PÚBLICA não muda ⇒ todos os wraps por caso continuam válidos ⇒ zero re-wrap.
 *
 * Roda SÓ no browser do membro, que é o único lugar onde as duas senhas coexistem.
 * O grep R-2.6 bloqueia este símbolo em apps/api e apps/web-admin.
 */
export async function rewrapMemberPrivateKey(
  privateKeyCiphertextB64: string,
  nonceB64: string,
  kekAntiga: Uint8Array,
  kekNova: Uint8Array,
): Promise<{ privateKeyCiphertext: string; nonce: string }> {
  const chaveAntiga = await crypto.subtle.importKey(
    'raw',
    toBuffer(kekAntiga),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  );
  // Lança se a KEK antiga estiver errada (tag AES-GCM) — o caller ABORTA sem enviar.
  const pkcs8 = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: toBuffer(b64ToBytes(nonceB64)) },
      chaveAntiga,
      toBuffer(b64ToBytes(privateKeyCiphertextB64)),
    ),
  );
  // `toBuffer` COPIA os bytes (ArrayBuffer.prototype.slice). Sem segurar a
  // referência, a cópia da privada ficaria viva pro GC SEM ser zerada — e zerar
  // só `pkcs8` daria uma garantia pela metade. Declarada FORA do try para o
  // finally alcançá-la mesmo se o importKey/encrypt lançar.
  let pkcs8Copia: ArrayBuffer | null = null;
  try {
    const chaveNova = await crypto.subtle.importKey(
      'raw',
      toBuffer(kekNova),
      { name: 'AES-GCM' },
      false,
      ['encrypt'],
    );
    const iv = crypto.getRandomValues(new Uint8Array(12)); // IV FRESCO — nunca reusar
    pkcs8Copia = toBuffer(pkcs8);
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, chaveNova, pkcs8Copia);
    return { privateKeyCiphertext: bytesToB64(new Uint8Array(ct)), nonce: bytesToB64(iv) };
  } finally {
    // NENHUMA cópia dos bytes da privada sobrevive a esta função.
    pkcs8.fill(0);
    if (pkcs8Copia) new Uint8Array(pkcs8Copia).fill(0);
  }
}

/**
 * Embrulha um DEK contra a chave PÚBLICA do membro (RSA-OAEP). Quem tem o DEK
 * (browser do denunciante no submit — Fatia 2) chama isto; não precisa de segredo
 * do membro. Saída ~256 bytes (RSA-2048).
 */
export async function wrapDekForMember(
  dekRaw: Uint8Array,
  publicKeyJwk: string,
): Promise<Uint8Array> {
  const publicKey = await crypto.subtle.importKey(
    'jwk',
    JSON.parse(publicKeyJwk) as JsonWebKey,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['wrapKey'],
  );
  const dekKey = await crypto.subtle.importKey(
    'raw',
    toBuffer(dekRaw),
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const wrapped = await crypto.subtle.wrapKey('raw', dekKey, publicKey, { name: 'RSA-OAEP' });
  return new Uint8Array(wrapped);
}

/**
 * Reabre o DEK com a privada do membro (Fatia 3 — leitura). Só roda no browser
 * do membro, com a CryptoKey de unwrapMemberPrivateKey.
 */
export async function unwrapDekWithMemberPrivate(
  wrappedDek: Uint8Array,
  privateKey: CryptoKey,
): Promise<Uint8Array> {
  const dekKey = await crypto.subtle.unwrapKey(
    'raw',
    toBuffer(wrappedDek),
    privateKey,
    { name: 'RSA-OAEP' },
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const exported = await crypto.subtle.exportKey('raw', dekKey);
  return new Uint8Array(exported);
}
